<template>
    <v-container class="payment-row">
        <v-row>
            <!-- We'll put the error messages in this element -->
            <div id="card-errors" role="alert"></div>

            <br />

            <div id="card-element" class="MyCardElement">
                <!-- Elements will create input elements here -->
            </div>

            <div v-if="isUpdatePage" id="upgrade-plan" class="">
                <v-checkbox
                    v-if="currentBillingHasPlan"
                    v-model="upgradePlan"
                    label="Do you want to also upgrade plan?"
                ></v-checkbox>
                <v-checkbox
                    v-else
                    v-model="newUser"
                    :rules="validateNewUserPlanCheckbox"
                    label="New Subcriber"
                    required
                ></v-checkbox>
            </div>
            <v-container>
                <v-select
                    v-if="showPlans"
                    v-model="subscriptionPlan"
                    :items="subscriptionPlans"
                    item-title="name"
                    item-value="stripe_plan_id"
                    label="Choose a subscription plan"
                    @update:modelValue="planSelected"
                >
                </v-select>
            </v-container>
        </v-row>

        <v-row align-content="end">
            <v-dialog v-model="dialog" width="500">
                <template #activator="{ props }">
                    <a class="pt-10" v-bind="props">Security Information</a>
                </template>

                <v-card>
                    <v-card-title class="headline" primary-title>
                        Security Information
                    </v-card-title>

                    <v-card-text>
                        Your credit card information is handled by Stripe's
                        secure payment system. Stripe is an industry leading
                        payment provider and is fully compliant with all the
                        relevant standards such as PCI. Smartdata does not
                        process or keep a copy of your credit card information
                        at any time except for the last 4 digits of your card
                        number and the expiry, so that we can display them to
                        you.
                    </v-card-text>

                    <v-divider />

                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="primary" text @click="dialog = false">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-spacer />

            <v-btn
                class="mt-5"
                large
                color="primary"
                :loading="btn_loading"
                :disabled="!btn_enabled"
                @click="getPaymentMethod"
            >
                {{ btn_text }}
                <v-icon right>{{ '$vuetify.icons.next' }} </v-icon>
            </v-btn>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'StripeForm',
    props: {
        billingInfo: {
            type: Object,
            default: function() {
                return null;
            }
        },
        isUpdateForm: {
            type: Boolean,
            default: function() {
                return false;
            }
        },
        billingPlans: {
            type: Array,
            default: function() {
                return [];
            }
        },
        successRedirectUrl: {
            type: String,
            default: '/'
        },
        submitLabel: {
            type: String,
            default: ''
        },
        useAssignedPlan: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            btn_enabled: false,
            btn_loading: false,
            dialog: false,
            stripe: null,
            elements: null,
            cardElement: null,
            upgradePlan: false,
            newUser: false,
            subscriptionPlans: this.billingPlans,
            subscriptionPlan: null,
            isUpdatePage: true
        };
    },
    computed: {
        normalizedBillingInfo() {
            return this.billingInfo?.billingAccountInfo || this.billingInfo || {};
        },
        currentBillingHasPlan() {
            return Boolean(this.normalizedBillingInfo.hasPlan);
        },
        assignedPlanSubscriptionId() {
            const plan = this.normalizedBillingInfo.plan || {};
            const stripeId = plan.stripe_price_id || plan.stripe_plan_id || null;
            return stripeId && stripeId !== '-1' ? stripeId : null;
        },
        canUseAssignedPlan() {
            return Boolean(this.useAssignedPlan && this.assignedPlanSubscriptionId);
        },
        btn_text() {
            if (this.submitLabel) return this.submitLabel;
            if (this.isPlanSelectPage()) return 'Start Subscription';
            return this.currentBillingHasPlan ? 'Update Card' : 'Start Subscription';
        },
        showPlans() {
            if (this.canUseAssignedPlan) return false;
            return this.upgradePlan || this.newUser ? true : false;
        },
        validateNewUserPlanCheckbox() {
            return [
                value =>
                    this.currentBillingHasPlan ||
                    value ||
                    'This is required. Current billing account has no billing plan and subscribing for the first time'
            ];
        }
    },
    watch: {
        billingInfo: {
            deep: true,
            immediate: true,
            handler() {
                this.refreshButtonState();
            }
        },
        subscriptionPlan() {
            this.refreshButtonState();
        },
        newUser() {
            if (!this.showPlans) {
                this.subscriptionPlan = null;
            }
            this.refreshButtonState();
        },
        upgradePlan() {
            if (!this.showPlans) {
                this.subscriptionPlan = null;
            }
            this.refreshButtonState();
        }
    },
    mounted() {
        // check if it's update page or user choose plan page
        if (String(window.location.href).includes('/accounts/plan/select')) {
            this.isUpdatePage = false;
        }

        if (!this.canInitializeStripe()) {
            this.btn_enabled = false;
            return;
        }

        // eslint-disable-next-line no-undef
        this.stripe = window.Stripe(window.NDSD_STRIPE_PK);
        this.elements = this.stripe.elements();

        const style = {
            base: {
                color: '#32325d',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': {
                    color: '#aab7c4'
                }
            },
            invalid: {
                color: '#fa755a',
                iconColor: '#fa755a'
            }
        };

        this.cardElement = this.elements.create('card', { style: style });
        setTimeout(() => {
            this.cardElement.mount('#card-element');
        }, 500);

        this.refreshButtonState();
    },
    methods: {
        getPaymentIntentClientSecret(data) {
            return data?.client_secret || data?.secret || '';
        },
        isPlanSelectPage() {
            return String(window.location.href).includes('/accounts/plan/select');
        },
        refreshButtonState() {
            if (this.isPlanSelectPage()) {
                this.btn_enabled = true;
                return;
            }

            if (this.currentBillingHasPlan) {
                this.btn_enabled = true;
                return;
            }

            if (this.canUseAssignedPlan) {
                this.btn_enabled = true;
                return;
            }

            this.btn_enabled = !!this.subscriptionPlan;
        },
        canInitializeStripe() {
            const host = window.location.hostname || '';
            const isLocalHost =
                host === 'localhost' || host === '127.0.0.1' || host === '::1';
            return window.location.protocol === 'https:' || isLocalHost;
        },
        planSelected() {
            this.refreshButtonState();
        },
        subscriptionConfirmed() {
            /**
             * Alerts and redirects the user when the subscription has been confirmed.
             */
            this.btn_enabled = false;
            this.btn_loading = false;
            window.alert('Your card was successfully verified.');
            if (!this.isUpdateForm) window.location = this.successRedirectUrl;
            else window.location.reload();
        },
        savePaymentMethod(paymentMethodObject) {
            /**
             * Saves the payment method to our DB and creates a subscription if necessary. Will also trigger
             * a 3DS authentication window (due to SCA) if it's needed.
             */
            axios
                .post(`/api/billing/payment/save`, paymentMethodObject)
                .then(response => response.data)
                .then(data => {
                    if (data.status === 'success') {
                        this.subscriptionConfirmed();
                    } else if (data.status === 'requires_action') {
                        const clientSecret =
                            this.getPaymentIntentClientSecret(data);
                        if (!clientSecret) {
                            this.btn_loading = false;
                            window.alert(
                                data.message ||
                                    'Stripe payment confirmation is unavailable.'
                            );
                            return;
                        }
                        // eslint-disable-next-line promise/no-nesting
                        this.stripe
                            .confirmCardPayment(clientSecret)
                            .then(result => {
                                if (result.error) {
                                    this.btn_loading = false;
                                    window.alert(result.error.message);
                                    // TODO: make this nicer
                                } else {
                                    // If the user successfully auths the 3DS activate their account
                                    axios
                                        .post('/api/billing/payment/activated', {
                                            billingAccountId: this
                                                .normalizedBillingInfo
                                                .billingAccountId
                                        })
                                        .then(response => {
                                            if (
                                                response.data?.status ===
                                                'success'
                                            ) {
                                                this.subscriptionConfirmed();
                                                return;
                                            }

                                            this.btn_loading = false;
                                            window.alert(
                                                response.data?.message ||
                                                    'Stripe payment confirmation is still pending.'
                                            );
                                        })
                                        .catch(() => {
                                            this.btn_loading = false;
                                            window.alert(
                                                'There was an error activating your account. Please contact ' +
                                                    'support.'
                                            );
                                        });
                                }
                            })
                            .catch(error => {
                                this.btn_loading = false;
                                window.alert(
                                    'There was an error saving your payment method.'
                                );
                                // TODO: make this nicer
                                throw error;
                            });
                    } else if (data.status === 'pending_activation') {
                        this.btn_loading = false;
                        window.alert(
                            data.message ||
                                'Stripe payment confirmation is still pending.'
                        );
                    } else {
                        this.btn_loading = false;
                        window.alert(
                            `There was an error while saving your payment method. ${
                                data.message || data.status
                            }`
                        );
                    }
                })
                // eslint-disable-next-line sonarjs/no-identical-functions
                .catch(error => {
                    this.btn_loading = false;
                    window.alert(
                        'There was an error saving your payment method.'
                    );
                    // TODO: make this nicer
                    throw error;
                });
        },
        getPaymentMethod() {
            /**
             * Gets a new payment method from the user using Stripe elements and Stripe.js
             */
            if (!this.stripe || !this.cardElement) {
                window.alert(
                    'Payment form is unavailable because Stripe requires HTTPS.'
                );
                return;
            }
            // Set subscription default plan to current plan if it's not an upgrade
            if (
                !String(window.location.href).includes('/accounts/plan/select')
            ) {
                if (
                    this.currentBillingHasPlan &&
                    this.normalizedBillingInfo.plan &&
                    (this.normalizedBillingInfo.plan.stripe_price_id != null ||
                        this.normalizedBillingInfo.plan.stripe_plan_id != null) &&
                    !this.upgradePlan &&
                    this.subscriptionPlan == null
                ) {
                    this.subscriptionPlan =
                        this.normalizedBillingInfo.plan.stripe_price_id ||
                        this.normalizedBillingInfo.plan.stripe_plan_id;
                }

                if (
                    !this.currentBillingHasPlan &&
                    this.canUseAssignedPlan &&
                    this.subscriptionPlan == null
                ) {
                    this.subscriptionPlan = this.assignedPlanSubscriptionId;
                }

                if (
                    !this.currentBillingHasPlan &&
                    this.subscriptionPlan == null
                ) {
                    window.alert(
                        'Plan is required. Kindly choose a plan to update billing account.'
                    );
                    return true;
                }
            }

            this.btn_loading = true;
            const billingInfo = this.normalizedBillingInfo;
            this.stripe
                .createPaymentMethod({
                    type: 'card',
                    card: this.cardElement,
                    billing_details: {
                        email: billingInfo.email,
                        name: billingInfo.contact_name
                    }
                })
                .then(result => {
                    if (result.error) {
                        throw result.error;
                    }

                    // Handle result.error or result.paymentMethod
                    result = result.paymentMethod;

                    let paymentMethod = {
                        billingAccountId: billingInfo.billingAccountId,
                        upgradePlan: this.upgradePlan,
                        newUser: this.newUser,
                        subscriptionPlan: this.subscriptionPlan,
                        id: result.id,
                        card: {
                            last4: result.card.last4,
                            exp:
                                result.card.exp_month +
                                '/' +
                                result.card.exp_year,
                            country: result.card.country,
                            brand: result.card.brand
                        }
                    };

                    this.savePaymentMethod(paymentMethod);
                })
                .catch(error => {
                    this.btn_loading = false;
                    window.alert(
                        'There was an error verifying your payment method.'
                    );
                    // TODO: make this nicer
                    throw error;
                });
        }
    }
};
</script>

<style scoped>
.MyCardElement {
    height: 40px;
    padding: 10px 12px;
    width: 100%;
    color: #32325d;
    background-color: white;
    border: 1px solid transparent;
    border-radius: 4px;

    box-shadow: 0 1px 3px 0 #e6ebf1;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
}

.MyCardElement--focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
}

.MyCardElement--invalid {
    border-color: #fa755a;
}

.MyCardElement--webkit-autofill {
    background-color: #fefde5 !important;
}

.payment-row {
    max-width: 400px;
}
</style>
